import { useState } from "react";
import { Plus } from "lucide-react";
import {
  useArchiveContact,
  useContacts,
  useSaveContact,
  type Contact,
} from "@/api/clients";
import { ContactForm } from "@/components/forms/ContactForm";
import { RequestError } from "@/components/RequestError";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { EmptyState } from "@/components/ui/empty-state";

type Editing = { mode: "new" } | { mode: "edit"; contact: Contact } | null;

export function ContactsPanel({ clientId, archived }: { clientId: string; archived: boolean }) {
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editing, setEditing] = useState<Editing>(null);
  const contacts = useContacts(clientId, includeArchived);
  const save = useSaveContact(clientId);
  const archive = useArchiveContact(clientId);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Checkbox
          id={`contacts-archived-${clientId}`}
          label="Show archived contacts"
          checked={includeArchived}
          onChange={setIncludeArchived}
        />
        {!archived && editing === null && (
          <Button size="sm" onClick={() => setEditing({ mode: "new" })}>
            <Plus className="h-4 w-4" aria-hidden />
            Add contact
          </Button>
        )}
      </div>
      {archived && <Alert>This client is archived. Restore it to add or edit contacts.</Alert>}

      {editing && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editing.mode === "new" ? "New contact" : `Edit ${editing.contact.name}`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ContactForm
              key={editing.mode === "edit" ? `${editing.contact.id}-${editing.contact.version}` : "new"}
              initial={editing.mode === "edit" ? editing.contact : null}
              submitLabel={editing.mode === "new" ? "Add contact" : "Save contact"}
              onSubmit={async (input) => {
                await save.mutateAsync({
                  input,
                  existing:
                    editing.mode === "edit"
                      ? { id: editing.contact.id, version: editing.contact.version }
                      : undefined,
                });
                setEditing(null);
              }}
              onCancel={() => setEditing(null)}
              onReload={() => {
                void contacts.refetch();
                setEditing(null);
              }}
            />
          </CardContent>
        </Card>
      )}

      <RequestError error={archive.error} onReload={() => void contacts.refetch()} />

      {contacts.isPending ? (
        <p className="text-muted-foreground">Loading contacts…</p>
      ) : contacts.isError ? (
        <RequestError error={contacts.error} />
      ) : contacts.data.length === 0 ? (
        <EmptyState
          title="No contacts yet"
          description="Add the people you work with. Tick “Receives invoices” for whoever should get invoices by e-mail."
        />
      ) : (
        <ul className="grid gap-3 md:grid-cols-2">
          {contacts.data.map((contact) => {
            const isArchived = contact.archived_at !== null;
            return (
              <li key={contact.id}>
                <Card className="h-full">
                  <CardContent className="flex flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{contact.name}</span>
                      {contact.is_primary && <Badge>Primary</Badge>}
                      {contact.receives_invoices && (
                        <Badge variant="success">Receives invoices</Badge>
                      )}
                      {isArchived && <Badge variant="muted">Archived</Badge>}
                    </div>
                    {contact.title && (
                      <p className="text-sm text-muted-foreground">{contact.title}</p>
                    )}
                    {contact.email && (
                      <a
                        href={`mailto:${contact.email}`}
                        className="break-all text-sm text-primary hover:underline"
                      >
                        {contact.email}
                      </a>
                    )}
                    {contact.phone && <p className="text-sm">{contact.phone}</p>}
                    {!archived && (
                      <div className="flex flex-wrap gap-2 pt-1">
                        {!isArchived && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditing({ mode: "edit", contact })}
                          >
                            Edit
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={archive.isPending}
                          onClick={() =>
                            archive.mutate({
                              id: contact.id,
                              archived: !isArchived,
                              version: contact.version,
                            })
                          }
                        >
                          {isArchived ? "Restore" : "Archive"}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
